import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Loader } from "lucide-react";
import { useFeeMargin, useReopenFeeMargin, useResolveFeeMargin } from "../hooks/useTreasury";
import { useCollapsed } from "../hooks/useCollapsed";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";

const sourceLabels = {
  actual: "Reported by partner",
  quoted: "Quoted",
  price_list: "Price list",
  spread: "Estimated from spread",
};

const negative = (n) => (n < -0.005 ? "text-red-600 font-semibold" : "text-gray-700");

// Read-only. "Pass-through" is the provider fee plus network fee we charge to
// cover the partner's cost; the margin is that minus what the partner
// actually charged. Only payouts whose partner fee was captured appear here,
// and only where that fee is in the payout's own currency. Partner fees that
// are estimates are kept on their own rows, apart from reported ones.
function FeeMarginReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading, isError, error } = useFeeMargin({ from, to });
  const { collapsed, toggle } = useCollapsed("treasury-fee-margin-undercharged");
  const { mutate: resolve, isPending: isResolving } = useResolveFeeMargin();
  const { mutate: reopen, isPending: isReopening } = useReopenFeeMargin();
  const markResolved = (references) => {
    const note = window.prompt(
      `Mark ${references.length} payout(s) as resolved.\n\nOptional note, for example what you changed (leave empty for none):`,
      "",
    );
    if (note === null) return; // cancelled
    resolve({ references, note });
  };
  const resolvedLabel = (resolved) =>
    `Resolved${resolved.resolvedBy ? ` by ${resolved.resolvedBy}` : ""} on ${new Date(resolved.resolvedAt).toLocaleDateString()}`;
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-6">
      <div className="p-4 text-sm text-gray-600 bg-gray-50 rounded-xl border border-gray-200">
        What each payout partner actually charged us for payouts, against the provider and network fees we charged
        to cover it. A negative pass-through margin means the partner cost more than we collected for it, before our
        own platform fee. Each row is the total for that partner, currency and fee type over the range; click a partner to see its payouts by date. Payouts without a captured partner fee are not counted (unknown is not zero). Default
        range is the last 30 days.
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-2 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-3 py-2 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        {(from || to) && (
          <button
            onClick={() => {
              setFrom("");
              setTo("");
            }}
            className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-800"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && (
        <div className="flex gap-2 items-center text-sm text-gray-500">
          <Loader className="w-5 h-5 animate-spin" /> Loading fee margin...
        </div>
      )}
      {isError && (
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">Error: {error.message}</p>
      )}

      {data && (
        <>
          {data.skippedNoRate > 0 && (
            <p className="px-4 py-3 text-sm text-amber-800 bg-amber-50 rounded-xl border border-amber-200">
              {data.skippedNoRate} payout(s) were left out because the partner fee was in another currency and no
              exchange rate was available.
            </p>
          )}
          {data.truncated && (
            <p className="px-4 py-3 text-sm text-amber-800 bg-amber-50 rounded-xl border border-amber-200">
              This range has more payouts than the report can read at once, so it shows the most recent ones. Pick a
              shorter range for the full picture.
            </p>
          )}
          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/50">
                    {[
                      "Partner",
                      "Currency",
                      "Partner fee is",
                      "Payouts",
                      "Pass-through charged",
                      "Partner charged",
                      "Pass-through margin",
                      "Margin with platform fee",
                      "Under-charged payouts",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:px-6"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.summary.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-6 py-8 text-sm text-center text-gray-500">
                        No payouts with a captured partner fee in this range yet
                      </td>
                    </tr>
                  )}
                  {data.summary.map((r) => {
                    const rowKey = `${r.provider}-${r.currency}-${r.source}`;
                    const isOpen = expanded.has(rowKey);
                    return (
                    <Fragment key={rowKey}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-700 capitalize md:px-6">
                        <button
                          onClick={() => toggleRow(rowKey)}
                          aria-expanded={isOpen}
                          className="flex gap-1.5 items-center capitalize"
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {r.provider}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 md:px-6">{r.currency}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 md:px-6">{sourceLabels[r.source] ?? r.source}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600 md:px-6">
                        {r.payouts}
                        {r.converted > 0 && (
                          <span
                            className="block text-xs text-gray-400"
                            title="The partner charged these in another currency (for example USD); converted at today's rate, so an estimate."
                          >
                            {r.converted} converted
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600 md:px-6">
                        {formatCurrencyAmount(r.passThroughCharged, r.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600 md:px-6">
                        {formatCurrencyAmount(r.partnerFee, r.currency)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right md:px-6 ${negative(r.passThroughMargin)}`}>
                        {formatCurrencyAmount(r.passThroughMargin, r.currency)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right md:px-6 ${negative(r.netMargin)}`}>
                        {formatCurrencyAmount(r.netMargin, r.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600 md:px-6">
                        {r.underChargedPayouts > 0
                          ? `${r.underChargedPayouts} (${formatCurrencyAmount(r.underChargedAmount, r.currency)})`
                          : "-"}
                        {r.resolvedPayouts > 0 && (
                          <span className="block text-xs text-emerald-700">{r.resolvedPayouts} resolved</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right md:px-6">
                        {r.underChargedPayouts > 0 && (
                          <button
                            onClick={() =>
                              markResolved(
                                (r.items ?? []).filter((p) => p.margin < -0.005 && !p.resolved).map((p) => p.reference),
                              )
                            }
                            disabled={isResolving}
                            className="block mb-1 ml-auto text-xs font-medium text-gray-600 hover:underline disabled:opacity-50"
                            title="After fixing the fee settings, mark this row's under-charged payouts as dealt with"
                          >
                            Mark resolved
                          </button>
                        )}
                        {r.underChargedPayouts > 0 && (
                          <Link
                            to={`/payout-partner-settings?search=${encodeURIComponent(r.provider)}`}
                            className="text-xs font-medium text-emerald-700 hover:underline"
                          >
                            Fee settings
                          </Link>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={10} className="px-4 py-3 md:px-6">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs tracking-wider text-left text-gray-500 uppercase">
                                {["Date", "Reference", "Amount", "Charged", "Partner fee", "Margin"].map((h) => (
                                  <th key={h} className="px-3 py-2 font-bold">
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {(r.items ?? []).map((p) => (
                                <tr key={p.reference}>
                                  <td className="px-3 py-2 text-gray-500">{new Date(p.date).toLocaleString()}</td>
                                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{p.reference}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">{formatCurrencyAmount(p.amount, r.currency)}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {formatCurrencyAmount(p.passThroughCharged, r.currency)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">{formatCurrencyAmount(p.partnerFee, r.currency)}</td>
                                  <td className={`px-3 py-2 text-right ${negative(p.margin)}`}>
                                    {formatCurrencyAmount(p.margin, r.currency)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {r.payouts > (r.items?.length ?? 0) && (
                            <p className="mt-2 text-xs text-gray-400">
                              Showing the {r.items.length} most recent of {r.payouts} payouts.
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className={`rounded border border-gray-200 bg-gray-50/50 ${collapsed ? "p-3" : "p-4"}`}>
            <button
              onClick={toggle}
              aria-expanded={!collapsed}
              className="flex gap-2 items-center text-sm font-bold text-[#013941]"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Payouts where the partner cost more than we charged ({data.underCharged.filter((p) => !p.resolved).length} outstanding{data.underCharged.some((p) => p.resolved) ? `, ${data.underCharged.filter((p) => p.resolved).length} resolved` : ""})
            </button>
            {!collapsed && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs tracking-wider text-left text-gray-500 uppercase">
                      {["Reference", "Partner", "Currency", "Amount", "Charged", "Partner fee", "Shortfall", "Date", ""].map(
                        (h) => (
                          <th key={h} className="px-3 py-2 font-bold">
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.underCharged.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                          None in this range
                        </td>
                      </tr>
                    )}
                    {data.underCharged.map((p) => (
                      <tr key={p.reference} className={p.resolved ? "opacity-60" : ""}>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{p.reference}</td>
                        <td className="px-3 py-2 text-gray-700 capitalize">{p.provider}</td>
                        <td className="px-3 py-2 text-gray-600">{p.currency}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatCurrencyAmount(p.amount, p.currency)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">
                          {formatCurrencyAmount(p.passThroughCharged, p.currency)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatCurrencyAmount(p.partnerFee, p.currency)}</td>
                        <td className="px-3 py-2 font-semibold text-right text-red-600">
                          {formatCurrencyAmount(p.shortfall, p.currency)}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{new Date(p.createdAt).toLocaleDateString()}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {p.resolved ? (
                            <span title={p.resolved.note || undefined} className="text-xs font-medium text-emerald-700">
                              {resolvedLabel(p.resolved)}
                              {p.resolved.note ? ` - ${p.resolved.note}` : ""}{" "}
                              <button
                                onClick={() => reopen(p.reference)}
                                disabled={isReopening}
                                className="ml-1 text-gray-500 hover:underline disabled:opacity-50"
                              >
                                Undo
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => markResolved([p.reference])}
                              disabled={isResolving}
                              className="px-3 py-1 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Mark resolved
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default FeeMarginReport;
